import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Avatar,
  IconButton,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  TextField,
  InputAdornment,
  Grid,
  Stack,
  Snackbar,
  Alert,
  alpha,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  VisibilityOff as HideIcon,
  Business as BusinessIcon,
  Close as CloseIcon,
  ArrowBack as BackIcon,
} from "@mui/icons-material";
import {
  getCustomers,
  deleteCustomer,
  patchCustomer,
} from "../../../Services/Services";
import OurCustomerForm from "./OurCustomerForm";

import { brandColor as brand } from "../../../theme";

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "4px",
    backgroundColor: "#f8fafc",
    transition: "all 0.2s ease",
    "& fieldset": { borderColor: "#e5e7eb" },
    "&:hover fieldset": { borderColor: "#c7d2fe" },
    "&.Mui-focused fieldset": { borderColor: brand, borderWidth: 1.5 },
  },
  "& .MuiInputLabel-root": { fontSize: "0.78rem", color: "#6b7280" },
  "& .MuiInputLabel-root.Mui-focused": { color: brand },
};

const CustomerList = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    customer: null,
  });
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const data = await getCustomers();
      setCustomers(Array.isArray(data) ? data : []);
    } catch {
      setSnackbar({
        open: true,
        message: "Failed to load customers.",
        severity: "error",
      });
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    if (!deleteDialog.customer) return;
    setDeleting(true);
    try {
      const result = await deleteCustomer(deleteDialog.customer.id);
      if (result) {
        setSnackbar({
          open: true,
          message: "Customer deleted successfully.",
          severity: "success",
        });
        fetchCustomers();
      } else {
        setSnackbar({
          open: true,
          message: "Failed to delete customer.",
          severity: "error",
        });
      }
    } catch {
      setSnackbar({
        open: true,
        message: "Error deleting customer.",
        severity: "error",
      });
    }
    setDeleting(false);
    setDeleteDialog({ open: false, customer: null });
  };

  const handleToggleActive = async (customer) => {
    try {
      const result = await patchCustomer(customer.id, {
        is_active: !customer.is_active,
      });
      if (result) {
        setSnackbar({
          open: true,
          message: `Customer ${customer.is_active ? "hidden" : "visible"} on site.`,
          severity: "success",
        });
        fetchCustomers();
      }
    } catch {
      setSnackbar({
        open: true,
        message: "Failed to update status.",
        severity: "error",
      });
    }
  };

  const handleEdit = (id) => {
    setEditId(id);
    setShowForm(true);
  };

  const handleAdd = () => {
    setEditId(null);
    setShowForm(true);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditId(null);
    fetchCustomers();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditId(null);
  };

  const getInitials = (name = "") =>
    name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase();

  const filtered = customers.filter((c) =>
    c.name?.toLowerCase().includes(search.toLowerCase()),
  );

  if (showForm) {
    return (
      <OurCustomerForm
        editId={editId}
        onSuccess={handleFormSuccess}
        onCancel={handleFormCancel}
      />
    );
  }

  return (
    <Box
      sx={{ px: 0, pt: 0, mt: -1, maxWidth: 1200, mx: "auto", width: "100%" }}
    >
      <Paper
        sx={{
          borderRadius: "4px",
          border: "1px solid #e5e7eb",
          boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}
      >
        {/* ── Header ── */}
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            backgroundColor: "#f8fafc",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: "4px",
                  bgcolor: alpha(brand, 0.1),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <BusinessIcon sx={{ color: brand, fontSize: 18 }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#111827",
                    fontSize: "1rem",
                    lineHeight: 1.2,
                    textAlign: "left",
                  }}
                >
                  Our Customers
                </Typography>
                <Typography
                  sx={{ fontSize: "0.7rem", color: "#6b7280", mt: 0.2 }}
                >
                  Manage customer logos displayed on the landing page
                </Typography>
              </Box>
              <Chip
                label={`${filtered.length} Customer${filtered.length !== 1 ? "s" : ""}`}
                size="small"
                sx={{
                  borderRadius: "4px",
                  bgcolor: alpha(brand, 0.1),
                  color: brand,
                  fontSize: "0.65rem",
                  fontWeight: 600,
                  height: 20,
                }}
              />
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                placeholder="Search customers..."
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ ...textFieldSx, width: 220 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#9ca3af", fontSize: 18 }} />
                    </InputAdornment>
                  ),
                  sx: { height: 34 },
                }}
              />
              <Button
                startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                onClick={handleAdd}
                size="small"
                variant="contained"
                sx={{
                  borderRadius: "4px",
                  textTransform: "none",
                  fontSize: "0.75rem",
                  height: 34,
                  px: 2,
                  bgcolor: brand,
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#4338ca", boxShadow: "none" },
                }}
              >
                Add Customer
              </Button>
            </Stack>
          </Box>
        </Box>

        {/* ── Content ── */}
        <Box sx={{ p: 2.5 }}>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 300,
              }}
            >
              <CircularProgress sx={{ color: brand }} />
            </Box>
          ) : filtered.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 8,
                border: "1.5px dashed #e5e7eb",
                borderRadius: "4px",
                bgcolor: "#f8fafc",
              }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "4px",
                  bgcolor: alpha(brand, 0.08),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 1.5,
                }}
              >
                <BusinessIcon sx={{ fontSize: 28, color: alpha(brand, 0.3) }} />
              </Box>
              <Typography
                sx={{ fontWeight: 600, color: "#374151", fontSize: "0.85rem" }}
              >
                No customers found
              </Typography>
              <Typography
                sx={{ fontSize: "0.72rem", color: "#9ca3af", mt: 0.5 }}
              >
                {search
                  ? "Try a different search term"
                  : "Add your first customer to get started"}
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {filtered.map((customer, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={customer.id}>
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: "4px",
                      border: "1px solid #e5e7eb",
                      overflow: "hidden",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: "#c7d2fe",
                        boxShadow: "0 4px 16px rgba(79,70,229,0.08)",
                      },
                      position: "relative",
                    }}
                  >
                    {/* Status indicator bar */}
                    <Box
                      sx={{
                        height: 3,
                        bgcolor: customer.is_active ? "#10b981" : "#ef4444",
                      }}
                    />

                    {/* Order badge */}
                    <Chip
                      label={`#${customer.display_order}`}
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 10,
                        right: 8,
                        borderRadius: "4px",
                        bgcolor: alpha(brand, 0.08),
                        color: brand,
                        fontSize: "0.58rem",
                        fontWeight: 700,
                        height: 18,
                        minWidth: 28,
                        zIndex: 2,
                      }}
                    />

                    {/* Logo Preview Area */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: 120,
                        bgcolor: "#fafbfc",
                        borderBottom: "1px solid #f1f5f9",
                        backgroundImage: customer.logo
                          ? "#fff"
                          : "none",
                        backgroundSize: "12px 12px",
                        backgroundPosition:
                          "0 0, 0 6px, 6px -6px, -6px 0px",
                        p: 1.5,
                      }}
                    >
                      {customer.logo ? (
                        <Box
                          component="img"
                          src={customer.logo}
                          alt={customer.name}
                          sx={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                            borderRadius: "2px",
                          }}
                        />
                      ) : (
                        <Avatar
                          variant="rounded"
                          sx={{
                            width: 72,
                            height: 72,
                            bgcolor: alpha(brand, 0.1),
                            color: brand,
                            fontSize: "1.4rem",
                            fontWeight: 800,
                            borderRadius: "4px",
                          }}
                        >
                          {getInitials(customer.name)}
                        </Avatar>
                      )}
                    </Box>

                    {/* Card Body */}
                    <Box sx={{ px: 1.5, py: 1.2 }}>
                      {/* Name */}
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: "#111827",
                          fontSize: "0.82rem",
                          lineHeight: 1.3,
                          mb: 0.5,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={customer.name}
                      >
                        {customer.name}
                      </Typography>

                      {/* Status + Initials */}
                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        sx={{ mb: 1 }}
                      >
                        <Chip
                          label={customer.is_active ? "Active" : "Inactive"}
                          size="small"
                          sx={{
                            borderRadius: "4px",
                            bgcolor: customer.is_active
                              ? "#ecfdf5"
                              : "#fef2f2",
                            color: customer.is_active ? "#059669" : "#dc2626",
                            fontSize: "0.6rem",
                            fontWeight: 600,
                            height: 18,
                          }}
                        />
                        {/* <Chip
                          label={
                            customer.initials || getInitials(customer.name)
                          }
                          size="small"
                          sx={{
                            borderRadius: "4px",
                            bgcolor: alpha(brand, 0.08),
                            color: brand,
                            fontSize: "0.6rem",
                            fontWeight: 700,
                            height: 18,
                          }}
                        /> */}
                      </Stack>

                      {/* Action Buttons */}
                      <Stack
                        direction="row"
                        spacing={0.5}
                        sx={{
                          pt: 1,
                          borderTop: "1px solid #f1f5f9",
                        }}
                      >
                        <Tooltip
                          title={
                            customer.is_active
                              ? "Hide from site"
                              : "Show on site"
                          }
                          arrow
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleToggleActive(customer)}
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: "4px",
                              bgcolor: customer.is_active
                                ? alpha("#10b981", 0.1)
                                : alpha("#ef4444", 0.1),
                              color: customer.is_active
                                ? "#10b981"
                                : "#ef4444",
                              "&:hover": {
                                bgcolor: customer.is_active
                                  ? alpha("#10b981", 0.2)
                                  : alpha("#ef4444", 0.2),
                              },
                            }}
                          >
                            {customer.is_active ? (
                              <ViewIcon sx={{ fontSize: 15 }} />
                            ) : (
                              <HideIcon sx={{ fontSize: 15 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(customer.id)}
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: "4px",
                              bgcolor: alpha(brand, 0.1),
                              color: brand,
                              "&:hover": { bgcolor: alpha(brand, 0.2) },
                            }}
                          >
                            <EditIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete" arrow>
                          <IconButton
                            size="small"
                            onClick={() =>
                              setDeleteDialog({ open: true, customer })
                            }
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: "4px",
                              bgcolor: alpha("#ef4444", 0.1),
                              color: "#ef4444",
                              "&:hover": { bgcolor: alpha("#ef4444", 0.2) },
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Paper>

      {/* ── Delete Dialog ── */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, customer: null })}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "4px",
            border: "1px solid #e5e7eb",
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            bgcolor: "#dc2626",
            px: 2.5,
            py: 1.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: "4px",
                bgcolor: "rgba(255,255,255,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <DeleteIcon sx={{ color: "#fff", fontSize: 16 }} />
            </Box>
            <Typography
              sx={{ fontWeight: 700, color: "#fff", fontSize: "0.9rem" }}
            >
              Delete Customer
            </Typography>
          </Stack>
          <IconButton
            onClick={() => setDeleteDialog({ open: false, customer: null })}
            size="small"
            sx={{
              color: "#fff",
              borderRadius: "4px",
              "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 2.5 }}>
          {deleteDialog.customer && (
            <Box sx={{ textAlign: "center", py: 1 }}>
              {deleteDialog.customer.logo ? (
                <Box
                  component="img"
                  src={deleteDialog.customer.logo}
                  alt={deleteDialog.customer.name}
                  sx={{
                    width: 80,
                    height: 60,
                    objectFit: "contain",
                    borderRadius: "4px",
                    border: "1px solid #e5e7eb",
                    mx: "auto",
                    mb: 1.5,
                    p: 0.5,
                    bgcolor: "#f8fafc",
                  }}
                />
              ) : (
                <Avatar
                  variant="rounded"
                  sx={{
                    width: 60,
                    height: 60,
                    bgcolor: alpha(brand, 0.1),
                    color: brand,
                    fontSize: "1.2rem",
                    fontWeight: 800,
                    borderRadius: "4px",
                    mx: "auto",
                    mb: 1.5,
                  }}
                >
                  {getInitials(deleteDialog.customer.name)}
                </Avatar>
              )}
              <Typography
                sx={{
                  fontWeight: 700,
                  color: "#111827",
                  fontSize: "0.9rem",
                  mb: 0.5,
                }}
              >
                {deleteDialog.customer.name}
              </Typography>
              <Typography sx={{ fontSize: "0.78rem", color: "#6b7280" }}>
                Are you sure you want to delete this customer? This action
                cannot be undone.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 1.5,
            bgcolor: "#fef2f2",
            borderTop: "1px solid #fecaca",
            gap: 1,
          }}
        >
          <Button
            onClick={() => setDeleteDialog({ open: false, customer: null })}
            size="small"
            sx={{
              borderRadius: "4px",
              textTransform: "none",
              fontSize: "0.75rem",
              px: 2,
              border: "1px solid #d1d5db",
              color: "#6b7280",
              boxShadow: "none",
              "&:hover": {
                borderColor: brand,
                color: brand,
                bgcolor: alpha(brand, 0.04),
                boxShadow: "none",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={deleting}
            variant="contained"
            size="small"
            startIcon={
              deleting ? (
                <CircularProgress size={14} sx={{ color: "#fff" }} />
              ) : (
                <DeleteIcon sx={{ fontSize: 15 }} />
              )
            }
            sx={{
              borderRadius: "4px",
              textTransform: "none",
              fontSize: "0.75rem",
              px: 2,
              bgcolor: "#dc2626",
              boxShadow: "none",
              "&:hover": { bgcolor: "#b91c1c", boxShadow: "none" },
              "&.Mui-disabled": { bgcolor: alpha("#dc2626", 0.5), color: "#fff" },
            }}
          >
            {deleting ? "Deleting..." : "Delete Customer"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{
    top: "80px !important",
  }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{
            borderRadius: "4px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerList;